import { Injectable, NgZone, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';

/**
 * Service that fixes rendering issues with CDK Virtual Scroll viewports.
 *
 * This service addresses a bug where the .cdk-virtual-scroll-spacer element
 * is not consistently placed as the first child of the viewport, causing
 * layout and scrolling issues. It uses a MutationObserver to detect when
 * virtual scroll viewports are added to the DOM and ensures the spacer
 * element is correctly positioned.
 *
 * @see https://github.com/angular/components/issues/27104
 */
@Injectable({
  providedIn: 'root'
})
export class VirtualScrollFixerService implements OnDestroy {
  private observer: MutationObserver | null = null;
  private timeoutIds: Set<number> = new Set();
  private renderer: Renderer2;

  constructor(
    rendererFactory: RendererFactory2,
    private ngZone: NgZone
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  init(): void {
    const existingOverlayContainer = document.querySelector('.cdk-overlay-container') as HTMLElement;
    if (existingOverlayContainer) {
      this.observeOverlayContainer(existingOverlayContainer);
    } else {
      this.observeBodyForOverlayContainer();
    }
  }

  private observeBodyForOverlayContainer(): void {
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              if (element.classList?.contains('cdk-overlay-container')) {
                this.switchToOverlayObserver(element);
                return;
              }
              this.fixVirtualScrollers(element);
            }
          });
        });
      });
      this.observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    });
  }

  private switchToOverlayObserver(overlayContainer: HTMLElement): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.observeOverlayContainer(overlayContainer);
  }

  private observeOverlayContainer(overlayContainer: HTMLElement): void {
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.fixVirtualScrollers(node as HTMLElement);
            }
          });
        });
      });
      this.observer.observe(overlayContainer, {
        childList: true,
        subtree: true
      });
    });
  }

  private fixVirtualScrollers(root: HTMLElement): void {
    if (root.matches?.('cdk-virtual-scroll-viewport')) {
      this.reorderSpacerInViewport(root);
      return;
    }
    const viewports = root.querySelectorAll('cdk-virtual-scroll-viewport');
    viewports.forEach((viewport) => this.reorderSpacerInViewport(viewport as HTMLElement));
  }

  private reorderSpacerInViewport(viewport: HTMLElement): void {
    const timeoutId = setTimeout(() => {
      const spacer = viewport.querySelector('.cdk-virtual-scroll-spacer') as HTMLElement;
      if (spacer && viewport.firstElementChild !== spacer) {
        this.renderer.insertBefore(viewport, spacer, viewport.firstElementChild);
      }
    });
    this.timeoutIds.add(timeoutId);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.timeoutIds.forEach(id => clearTimeout(id));
    this.timeoutIds.clear();
  }
}

